import { Check, Languages, Monitor, Moon, Sun } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AVAILABLE_LOCALES, changeLocale } from '@/i18n';
import { cn } from '@/lib/cn';
import { type ThemeMode, useTheme } from '@/providers/ThemeProvider';

const THEME_ICONS: Record<ThemeMode, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

const THEME_ORDER: ThemeMode[] = ['light', 'dark', 'system'];

/** Закрытие меню по клику вне и по Escape — общее поведение для обоих переключателей. */
function useDismiss(onDismiss: () => void) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) onDismiss();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onDismiss();
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onDismiss]);

  return ref;
}

const MENU_CLASSES =
  'absolute right-0 top-full z-40 mt-1 min-w-[168px] overflow-hidden rounded-[10px] border border-border bg-surface p-1 shadow-float animate-in';

const ITEM_CLASSES =
  'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors duration-150';

/**
 * Выбор темы списком, а не по кругу: до нужной темы всегда один клик,
 * а не два-три (F-36).
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const ref = useDismiss(() => setOpen(false));

  const Icon = THEME_ICONS[theme];

  const labels: Record<ThemeMode, string> = {
    light: t('themeLight'),
    dark: t('themeDark'),
    system: t('themeSystem'),
  };

  return (
    <div className={cn('relative', className)} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        title={`${t('theme')}: ${labels[theme]}`}
        aria-label={`${t('theme')}: ${labels[theme]}`}
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-[10px] text-muted',
          'transition-colors duration-150 hover:bg-surface-2 hover:text-text',
          open && 'bg-surface-2 text-text',
        )}
      >
        <Icon className="h-[17px] w-[17px]" />
      </button>

      {open && (
        <div className={MENU_CLASSES} role="menu">
          {THEME_ORDER.map((mode) => {
            const ModeIcon = THEME_ICONS[mode];
            const active = theme === mode;
            return (
              <button
                key={mode}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setTheme(mode);
                  setOpen(false);
                }}
                className={cn(
                  ITEM_CLASSES,
                  active ? 'bg-accent-soft font-semibold text-accent' : 'text-text hover:bg-surface-2',
                )}
              >
                <ModeIcon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                <span className="flex-1">{labels[mode]}</span>
                {active && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Выбор языка. Тоже списком — чтобы поведение совпадало с темой. */
export function LocaleToggle({ className }: { className?: string }) {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const ref = useDismiss(() => setOpen(false));

  const current = AVAILABLE_LOCALES.find((locale) => locale.code === i18n.language);

  return (
    <div className={cn('relative', className)} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        title={`${t('language')}: ${current?.nativeName ?? i18n.language}`}
        aria-label={`${t('language')}: ${current?.nativeName ?? i18n.language}`}
        className={cn(
          'flex h-9 items-center gap-1.5 rounded-[10px] px-2.5 text-[13px] font-medium text-muted',
          'transition-colors duration-150 hover:bg-surface-2 hover:text-text',
          open && 'bg-surface-2 text-text',
        )}
      >
        <Languages className="h-[17px] w-[17px]" />
        <span className="uppercase">{i18n.language}</span>
      </button>

      {open && (
        <div className={MENU_CLASSES} role="menu">
          {AVAILABLE_LOCALES.map((locale) => {
            const active = locale.code === i18n.language;
            return (
              <button
                key={locale.code}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  changeLocale(locale.code);
                  setOpen(false);
                }}
                className={cn(
                  ITEM_CLASSES,
                  active ? 'bg-accent-soft font-semibold text-accent' : 'text-text hover:bg-surface-2',
                )}
              >
                <span className="w-4 shrink-0 text-center" aria-hidden>
                  {locale.flag}
                </span>
                <span className="flex-1">{locale.nativeName}</span>
                {active && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
